import React, { useEffect, useState } from "react";
import { LEARN } from "../theme";
import LearnSky from "./LearnSky";

/** Single source of the site navbar clearance for every Learn page. */
export function useNavOffset() {
  const [compact, setCompact] = useState(() => typeof window !== "undefined" && window.innerWidth < LEARN.bp.md);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${LEARN.bp.md - 1}px)`);
    const apply = () => setCompact(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return { compact, navOffset: compact ? LEARN.navOffsetCompact : LEARN.navOffset };
}

/** Responsive breakpoint hook (media-query based, no resize listeners). */
export function useBreakpoint() {
  const get = () => {
    if (typeof window === "undefined") return "lg";
    const w = window.innerWidth;
    if (w < LEARN.bp.sm) return "xs";
    if (w < LEARN.bp.md) return "sm";
    if (w < LEARN.bp.lg) return "md";
    return "lg";
  };
  const [bp, setBp] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const qs = [LEARN.bp.sm, LEARN.bp.md, LEARN.bp.lg].map((px) => window.matchMedia(`(min-width: ${px}px)`));
    const apply = () => setBp(get());
    qs.forEach((q) => q.addEventListener("change", apply));
    return () => qs.forEach((q) => q.removeEventListener("change", apply));
  }, []);
  return bp;
}

/**
 * Page frame for hub + map screens (navbar stays). Owns background, nav
 * clearance, max width and the soft Pixley sky. Games use <LearnStage>.
 */
export default function LearnShell({ children, maxWidth = 1240, background, padBottom = 64, style, sky = true, tint }) {
  const { navOffset } = useNavOffset();
  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        fontFamily: LEARN.font,
        color: LEARN.color.ink,
        background: background || "transparent",
        paddingTop: navOffset,
        paddingBottom: padBottom,
        ...style,
      }}
    >
      {sky && <LearnSky tint={tint} />}
      <div style={{ position: "relative", zIndex: 1, maxWidth, margin: "0 auto", padding: "0 clamp(16px, 3vw, 32px)" }}>{children}</div>
    </div>
  );
}
